import React from 'react';
import { Card, CardSkeleton, Badge } from '@/components/ui';
import type { PerformanceMetrics } from '@/services/admin/AdminService';

interface PerformanceMetricsCardProps {
  metrics: PerformanceMetrics | null;
  loading: boolean;
}

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const getErrorRateVariant = (rate: number): 'success' | 'warning' | 'error' => {
  if (rate < 1) return 'success';
  if (rate < 5) return 'warning';
  return 'error';
};

export const PerformanceMetricsCard: React.FC<PerformanceMetricsCardProps> = ({
  metrics,
  loading,
}) => {
  if (loading) {
    return <CardSkeleton />;
  }

  if (!metrics) {
    return (
      <Card padding="lg">
        <p className="text-secondary-500">No performance metrics available</p>
      </Card>
    );
  }

  return (
    <Card padding="lg">
      <h3 className="text-lg font-semibold text-secondary-900 mb-4">
        Performance
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4">
          <p className="text-3xl font-bold text-primary-600">
            {formatDuration(metrics.avg_response_time_ms)}
          </p>
          <p className="text-sm font-medium text-secondary-700">
            Avg Response Time
          </p>
        </div>

        <div className="text-center p-4">
          <div className="flex items-center justify-center gap-2">
            <p className="text-3xl font-bold text-secondary-900">
              {metrics.error_rate_percent.toFixed(1)}%
            </p>
            <Badge variant={getErrorRateVariant(metrics.error_rate_percent)}>
              {metrics.error_rate_percent < 1
                ? 'Healthy'
                : metrics.error_rate_percent < 5
                  ? 'Warning'
                  : 'High'}
            </Badge>
          </div>
          <p className="text-sm font-medium text-secondary-700">Error Rate</p>
        </div>

        <div className="text-center p-4">
          <p className="text-3xl font-bold text-primary-600">
            {metrics.requests_last_24h}
          </p>
          <p className="text-sm font-medium text-secondary-700">
            Requests (24h)
          </p>
        </div>

        <div className="text-center p-4">
          <p className="text-3xl font-bold text-primary-600">
            {metrics.requests_last_7d}
          </p>
          <p className="text-sm font-medium text-secondary-700">
            Requests (7d)
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-secondary-500 border-t border-secondary-100 pt-4">
        <span>Errors in last 24h: {metrics.errors_last_24h}</span>
      </div>
    </Card>
  );
};

export default PerformanceMetricsCard;
